import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LoadingButton from '@mui/lab/LoadingButton';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState, Fragment } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useParams, useNavigate } from 'react-router-dom';
import { FieldMappingProvider } from '../../../../reusable-components/formFieldPairs/FieldMappingContext';
import FieldMapping from '../../../../reusable-components/formFieldPairs/FieldMapping';
import {
  changeInvoiceField,
  getInvoiceLog,
  invoiceGLItemsForm,
  getInvoiceGls,
  getInvoiceBreakdown,
  parseGls,
} from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import usePrompt from '../../../../reusable-components/prompt/usePrompt';
import Scrollbar from '../../../../reusable-components/scrollbar';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { twoDecimal } from '../../../../utils/formatNumber';
import CategorySelector from './reusable/CategorySelector';
import { InvoiceGLItemParsedField } from '../../../../utils/globalEnums';
import { bobAIIcon } from '../../../../reusable-components/bobAI/bobAI';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';

GlItemsForm.propTypes = {
  glRows: PropTypes.array,
  setHighlightMissingGls: PropTypes.func,
  invoiceFormRef: PropTypes.any,
  invoiceFacilityId: PropTypes.any,
  unsavedData: PropTypes.any,
  setUnsavedData: PropTypes.func,
  highlightItem: PropTypes.object,
  invoiceSent: PropTypes.bool,
  invoiceDataLoaded: PropTypes.bool,
};
export default function GlItemsForm({
  glRows = [],
  setHighlightMissingGls,
  invoiceFormRef,
  invoiceFacilityId,
  unsavedData,
  setUnsavedData,
  highlightItem,
  invoiceSent,
  invoiceDataLoaded,
}) {
  const { id } = useParams();

  const dispatch = useDispatch();
  const {
    data: {
      invoiceData: { categories: cats, manualCategories, subcategories: subcats, manualSubcategories },
      invoiceItemData,
      invoiceGLItems,
    },
  } = useSelector((state) => state.invoicing);
  const { enqueueSnackbar } = useSnackbar();
  const [editGlItems, setEditGlItems] = useState(false);
  const [itemsMissingGls, setItemsMissingGls] = useState(false);
  const [expand, setExpand] = useState(false);
  const [glParseLoading, setGlParseLoading] = useState(false);
  const scrollbarRef = useRef(null);
  const manualInvoice = invoiceItemData?.invoice?.sourceMethod === 'Manual';

  const categories = !manualInvoice ? cats : manualCategories || [];
  const subcategories = !manualInvoice ? subcats : manualSubcategories || [];
  const [canEdit, setCanEdit] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [autoGlSaved, setAutoGlSaved] = useState(false);

  const { isEditable: hasEditAccess } = useIsInvoiceEditable();

  const navigate = useNavigate();

  const {
    control,
    setValue,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
    reset,
    trigger,
  } = useForm({
    mode: 'onChange',
  });
  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'invoiceGlValues',
  });
  const [isEditing, setIsEditing] = useState({});
  const [hoveringIndex, setHoveringIndex] = useState(null);
  usePrompt(editGlItems);

  const handleFocus = (e) => {
    setIsEditing((prev) => ({ ...prev, [e?.target?.name]: true }));
    e.target.select();
  };
  const handleHover = (e) => {
    setIsEditing((prev) => ({ ...prev, [e?.target?.name]: true }));
    e.target.select();
  };
  const handleBlur = (e) => {
    setIsEditing((prev) => ({ ...prev, [e?.target?.name]: false }));
  };

  useEffect(() => {
    setCanEdit(invoiceGLItems.length > 0 || ((invoiceGLItems.length === 0 || manualInvoice) && glRows.length === 0));
  }, [canEdit, invoiceGLItems, manualInvoice, glRows]);
  //update disable/enable gl save button
  useEffect(() => {
    // if (dataLoaded) {
    const keysToCheck = ['agoraCategoryId', 'agoraSubcategoryId', 'amount', 'shipping', 'memo', 'deleteFlag'];
    if (unsavedData != null) {
      const onEdit = getValues('invoiceGlValues')?.map(
        (item, index) =>
          !keysToCheck.every((key) => {
            const value1 = item[key];
            const value2 = glRows[index]?.[key];
            if (!Number.isNaN(+value2)) return Number(value1) === Number(value2);
            return (value1 === null && value2 === '') || (value1 === '' && value2 === null) || value1 === value2;
          })
      );
      setEditGlItems(onEdit.some((edit) => edit));
    } else {
      setEditGlItems(false);
    }
    // }
  }, [unsavedData]);
  useEffect(() => {
    if (highlightItem?.form === 'glcodes' && scrollbarRef.current) {
      const highlightedComment = document.getElementById(`item-${highlightItem?.id}`);
      if (highlightedComment) {
        const container = scrollbarRef.current;
        const commentTop = highlightedComment.offsetTop;
        container.scrollTo({
          top: commentTop,
          behavior: 'smooth',
        });
      }
    }
  }, [highlightItem]);

  useEffect(() => {
    if (!manualInvoice) return;
    if (invoiceGLItems.length !== 0) return;
    if (!glRows || glRows.length === 0) return;

    const save = async () => {
      setAutoGlSaved(true);
      await glFormSave(glRows, 'BobAI');
    };
    if (!autoGlSaved) save();
  }, [glRows]);

  function facilitiesMismatch() {
    const invoiceFacility = invoiceFacilityId ?? 0;
    const glItemFacility = glRows?.[0]?.facilityId ?? 0;
    return invoiceFacility && glItemFacility && invoiceFacility !== glItemFacility;
  }
  useEffect(() => {
    resetGlData();
    setEditGlItems(false);
  }, [invoiceFacilityId]);
  useEffect(() => {
    resetGlData();
  }, [glRows]);
  useEffect(() => {
    setItemsMissingGls(invoiceItemData?.invoiceItems?.filter((item) => item.subcategoryId === 0)?.length > 0);
  }, [invoiceItemData?.invoiceItems]);

  const resetGlData = () => {
    if (facilitiesMismatch()) remove();
    else {
      reset({
        invoiceGlValues: glRows,
      });
    }
    setUnsavedData({});
  };

  const handleDeleteGl = async (index) => {
    setValue(`invoiceGlValues.${index}.deleteFlag`, true);
    setUnsavedData(watch(`invoiceGlValues`));
  };

  const handleGlsFormSave = async () => {
    const glItemsValues = getValues('invoiceGlValues');
    const formSaved = await invoiceFormRef?.validateAndSave();
    if (formSaved) {
      //add rows that weren't changed but should be saved in the db
      if (invoiceGLItems?.length === 0 && glItemsValues?.length > 0) {
        setUnsavedData((prev) => {
          glItemsValues.forEach((row) => {
            if (!prev[row.id] && !row.deleteFlag) {
              prev[row.id] = row;
            }
          });

          return prev;
        });
      }
      await glFormSave(Object.values(unsavedData));
    }
  };
  const glFormSave = async (gls, addedBy = '') => {
    const glItemsValues = getValues('invoiceGlValues');
    const response = await dispatch(invoiceGLItemsForm({ invoiceGlItems: gls, addedBy }));

    if (addedBy !== 'BobAI') {
      //if was added automatically dont show snackbar
      enqueueSnackbar(response.message ? response.message : 'Error updating GL Items.', {
        variant: response.success ? 'success' : 'error',
        persist: response.keepFormOpen,
      });
    }

    if (response.success) {
      setUnsavedData({});
      dispatch(getInvoiceBreakdown(id));
      const glItems = glItemsValues.filter((x) => x.deleteFlag !== true);
      const newShipping = glItems.reduce(
        (accumulator, currentValue) => parseFloat(accumulator) + parseFloat(currentValue.shipping),
        0
      );
      const total =
        glItems
          ?.map((i) => Number(i.amount) + Number(i.shipping))
          ?.reduce((accumulator, current) => accumulator + current, 0) ?? 0;
      if (manualInvoice && invoiceItemData?.invoiceItems.length === 0) {
        await dispatch(changeInvoiceField(id, { key: 'Total', value: total }));
        await dispatch(changeInvoiceField(id, { key: 'Shipping', value: newShipping }));
      }

      if (total > 0) await dispatch(changeInvoiceField(id, { key: 'InvoiceType', value: 'Invoice' }));
      else if (total < 0) await dispatch(changeInvoiceField(id, { key: 'InvoiceType', value: 'CreditMemo' }));
      await dispatch(getInvoiceLog(id));
      await dispatch(getInvoiceGls(id));
    }
  };

  const handleAddGl = async () => {
    const result = await trigger('invoiceGlValues');
    const glItemsValues = getValues('invoiceGlValues');
    const glItems = glItemsValues.filter((x) => x.deleteFlag !== true);
    const totalGls =
      glItems?.map((i) => Number(i.amount))?.reduce((accumulator, current) => accumulator + current, 0) ?? 0;

    if (result) {
      const newRow = {
        id: watch('invoiceGlValues').length,
        glItemId: 0,
        invoiceId: id,
        facilityId: invoiceFacilityId,
        glCode: '',
        agoraCategoryId: '',
        agoraCategory: '',
        agoraSubcategoryId: '',
        agoraSubcategory: '',
        amount:
          manualInvoice && invoiceItemData?.invoice?.total > totalGls ? invoiceItemData?.invoice?.total - totalGls : 0,
        shipping: 0,
        memo: '',
        referenceNumber: '',
        deleteFlag: false,
      };
      insert(0, newRow);

      setTimeout(() => {
        if (selectedFieldRef.current[0]) {
          selectedFieldRef.current[0].focus();
        }
      }, 0);
    }
  };

  const handleGlCategoryChange = (newValue, item, index, nonDependantGls) => {
    setValue(`invoiceGlValues.${index}.agoraCategoryId`, newValue?.categoryId);
    setValue(`invoiceGlValues.${index}.agoraCategory`, newValue?.category);
    if (!nonDependantGls) {
      setValue(`invoiceGlValues.${index}.agoraSubcategoryId`, '');
      setValue(`invoiceGlValues.${index}.agoraSubcategory`, '');
      setValue(`invoiceGlValues.${index}.glCode`, '');
    }
    const updatedRow = {
      ...item,
      agoraCategoryId: newValue?.categoryId,
      agoraCategory: newValue?.category,
      ...(!nonDependantGls ? { agoraSubcategoryId: '', agoraSubcategory: '', glCode: '' } : {}),
    };

    setUnsavedData((prev) => ({
      ...prev,
      [item?.id]: updatedRow,
    }));
  };
  const handleGlSubcategoryChange = (newValue, item, index, nonDependantGls) => {
    if (newValue && newValue.value === 'add-gl-code') {
      navigate('/dashboard/invoicing/vendors?tab=1');
      return;
    }
    setValue(`invoiceGlValues.${index}.agoraSubcategoryId`, newValue.subcategoryId);
    setValue(`invoiceGlValues.${index}.agoraSubcategory`, newValue.subcategory);
    setValue(`invoiceGlValues.${index}.glCode`, newValue.glCode);
    const { category } =
      categories
        ?.filter((category) => category?.facilityId === item?.facilityId)
        ?.find((item) => item.categoryId === newValue.categoryId) || {};
    if (!nonDependantGls) {
      //set the right category
      setValue(`invoiceGlValues.${index}.agoraCategoryId`, newValue.categoryId);
      setValue(`invoiceGlValues.${index}.agoraCategory`, category ?? '');
    }

    const updatedRow = {
      ...item,
      agoraSubcategoryId: newValue.subcategoryId,
      agoraSubcategory: newValue.subcategory,
      glCode: newValue.glCode,
      ...(!nonDependantGls ? { agoraCategoryId: newValue.categoryId, agoraCategory: category ?? '' } : {}),
    };
    setUnsavedData((prev) => ({
      ...prev,
      [item?.id]: updatedRow,
    }));
  };
  const selectedFieldRef = useRef([]);
  return (
    <>
      <Box sx={mainBoxStyle}>
        <form onSubmit={handleSubmit(handleGlsFormSave)}>
          <Grid container alignItems="center" justifyContent={'space-between'} sx={{ padding: '0 10px' }}>
            <Grid item>
              <Grid container alignItems="center">
                <Grid item>
                  <Typography
                    color="info.main"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 'bold',
                      alignContent: 'center',
                      pt: watch('invoiceGlValues')?.length > 0 ? 0 : 1,
                      mt: invoiceSent ? 1 : 0,
                    }}
                  >
                    GL Codes
                  </Typography>
                </Grid>
                <Grid item>
                  {!manualInvoice && itemsMissingGls && (
                    <Tooltip title={'invoice items are missing gl codes'} arrow="down">
                      <IconButton
                        color="error"
                        onClick={() => {
                          setHighlightMissingGls(true);
                        }}
                      >
                        <ErrorOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              {hasEditAccess && (
                <Grid container alignItems="center" spacing={1}>
                  {manualInvoice && invoiceItemData?.invoice?.facilityId !== 0 && glRows?.length === 0 && (
                    <Grid item>
                      <LoadingButton
                        size="small"
                        color="secondary"
                        loading={glParseLoading}
                        onClick={async () => {
                          setGlParseLoading(true);
                          const res = await dispatch(parseGls(id));
                          enqueueSnackbar(res.data.message ? res.data.message : 'Failed to parse GL Items.', {
                            variant: res.data.success ? 'success' : 'error',
                            persist: res.data.keepFormOpen,
                          });
                          dispatch(getInvoiceBreakdown(id));

                          setGlParseLoading(false);
                        }}
                        sx={{
                          minWidth: 0,
                          borderRadius: '50%',
                          px: 0.5,
                          py: 0.2,
                          fontSize: '10px',
                          height: 'fit-content',
                          mt: watch('invoiceGlValues')?.length > 0 ? 0 : 1,
                        }}
                      >
                        <Tooltip title="Parse GL Items">
                          <span>
                            <Iconify icon="mdi:auto-fix" width={20} height={20} />
                          </span>
                        </Tooltip>
                      </LoadingButton>
                    </Grid>
                  )}
                  <Grid item>
                    {!canEdit && !manualInvoice && (
                      <Button
                        variant="outlined"
                        type="button"
                        onClick={() => setConfirmEdit(true)}
                        size="small"
                        color="secondary"
                        sx={{
                          px: 0.5,
                          py: 0.2,
                          fontSize: '10px',
                          height: 'fit-content',
                          mt: watch('invoiceGlValues')?.length > 0 ? 0 : 1,
                        }}
                      >
                        <Iconify icon="flowbite:edit-outline" width={15} height={15} /> Edit
                      </Button>
                    )}
                  </Grid>

                  <Grid item>
                    {canEdit && invoiceFacilityId !== 0 && invoiceFacilityId !== '' && (
                      <Box sx={{ width: '100%' }}>
                        <Button
                          variant="outlined"
                          type="button"
                          onClick={handleAddGl}
                          size="small"
                          disabled={glParseLoading}
                          color="secondary"
                          sx={{
                            fontSize: '10px',
                            mt: watch('invoiceGlValues')?.length > 0 ? 0 : 1,
                          }}
                        >
                          <Iconify icon="mdi:plus" width={15} height={15} /> GL Code
                        </Button>
                      </Box>
                    )}
                  </Grid>
                  <Grid item>
                    {watch('invoiceGlValues')?.length > 0 && (
                      <Tooltip
                        title={`${!expand ? 'more' : 'less'}`}
                        sx={{ display: !watch('invoiceGlValues')?.length > 0 ? 'none' : '' }}
                      >
                        <IconButton onClick={() => setExpand(!expand)} sx={{ color: 'info.main' }}>
                          <Iconify icon={`ic:twotone-expand-${!expand ? 'more' : 'less'}`} width={26} height={26} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>

          {watch('invoiceGlValues') && (
            <Scrollbar
              sx={{ maxHeight: expand ? '650px' : '250px', padding: '0 10px 10px 10px' }}
              scrollableNodeProps={{ ref: scrollbarRef }}
            >
              <List sx={{ py: 0.5 }}>
                <>
                  {watch('invoiceGlValues').map((item, index) => {
                    const watchCategory = watch(`invoiceGlValues.[${index}].agoraCategoryId`) || 0;
                    const watchSubcategory = watch(`invoiceGlValues.[${index}].agoraSubcategoryId`) || 0;
                    const watchGlCode = watch(`invoiceGlValues.[${index}].glCode`);
                    const watchExistingGls = watch('invoiceGlValues')?.map((x) => ({
                      glCode: x.glCode,
                      facilityId: x.facilityId,
                    }));
                    const watchShipping = watch(`invoiceGlValues.[${index}].shipping`);
                    const watchAmount = watch(`invoiceGlValues.[${index}].amount`);
                    const watchMemo = watch(`invoiceGlValues.[${index}].memo`);
                    const facilityCategories = categories.filter((cat) => cat?.facilityId === item?.facilityId);
                    const facilitySubcategories = subcategories.filter((sub) => sub?.facilityId === item?.facilityId);
                    const parsedFields = item.parsedFields ?? 0;
                    const watchDeleteFlag = watch(`invoiceGlValues.[${index}].deleteFlag`);
                    return (
                      <Fragment key={item.id}>
                        <Box
                          id={`item-${item.glItemId}`}
                          sx={{
                            backgroundColor:
                              highlightItem?.id !== 0 && highlightItem?.id === item.glItemId ? '#d8ebf9' : '#fff',
                            opacity: watchDeleteFlag ? 0.3 : 1,
                          }}
                        >
                          {' '}
                          <FieldMappingProvider formType="glItems" parsedFields={parsedFields}>
                            <ListItem key={index} sx={{ padding: 0.2, display: 'block' }}>
                              <Stack spacing={1}>
                                {' '}
                                {facilityCategories?.length > 0 && (
                                  <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                    <Typography
                                      sx={{
                                        ...fieldStyle,
                                        ...(hoveringIndex !== null && index === hoveringIndex
                                          ? { color: 'error.main' }
                                          : {}),
                                      }}
                                      variant="standard"
                                    >
                                      Category:
                                    </Typography>
                                    <>{(parsedFields & InvoiceGLItemParsedField.category) > 0 && bobAIIcon()}</>
                                    {hasEditAccess && canEdit ? (
                                      <Controller
                                        name={`invoiceGlValues.${index}.agoraCategoryId`}
                                        control={control}
                                        rules={{
                                          required:
                                            !item.deleteFlag &&
                                            (watchSubcategory === 0 ||
                                              !(
                                                watchSubcategory !== 0 &&
                                                facilitySubcategories.find((x) => x.subcategoryId === watchSubcategory)
                                                  ?.categoryId === 0
                                              )),
                                        }}
                                        render={({ field }) => (
                                          <CategorySelector
                                            ref={(el) => {
                                              selectedFieldRef.current[index] = el;
                                            }}
                                            isCategory
                                            currentCategory={watchCategory}
                                            currentSubcategory={watchSubcategory}
                                            currentGlCode={watchGlCode}
                                            existingGlCodes={watchExistingGls}
                                            handleChange={handleGlCategoryChange}
                                            hasEditAccess={hasEditAccess}
                                            disabled={!hasEditAccess || !invoiceDataLoaded}
                                            error={errors?.invoiceGlValues?.[index]?.agoraCategoryId}
                                            textFieldProps={{
                                              InputProps: {
                                                endAdornment: (
                                                  <>
                                                    {!invoiceDataLoaded ? (
                                                      <CircularProgress color="inherit" size={16} />
                                                    ) : null}
                                                  </>
                                                ),
                                                sx: valueStyle,
                                              },
                                            }}
                                            item={item}
                                            facilityId={item?.facilityId}
                                            index={index}
                                          />
                                        )}
                                      />
                                    ) : (
                                      <TextField
                                        variant="standard"
                                        value={
                                          facilityCategories?.find((x) => x.categoryId === watchCategory)?.category ||
                                          ''
                                        }
                                        sx={{ flexGrow: 1 }}
                                        disabled
                                        InputProps={{
                                          endAdornment: (
                                            <>
                                              {!invoiceDataLoaded ? (
                                                <CircularProgress color="inherit" size={16} />
                                              ) : null}
                                            </>
                                          ),
                                          sx: valueStyle,
                                        }}
                                        autoComplete="off"
                                      />
                                    )}
                                    {hasEditAccess && canEdit && (
                                      <IconButton
                                        color="error"
                                        onClick={() => {
                                          handleDeleteGl(index);
                                          setHoveringIndex(null);
                                        }}
                                        onMouseEnter={() => setHoveringIndex(index)}
                                        onMouseLeave={() => setHoveringIndex(null)}
                                        sx={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent background
                                          borderRadius: '50%',
                                          padding: '0',
                                          color: 'error',
                                          '&:hover': {
                                            backgroundColor: 'rgba(255, 0, 0, 0.2)', // Red hover effect
                                          },
                                        }}
                                      >
                                        <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error' }} />
                                      </IconButton>
                                    )}
                                  </Stack>
                                )}
                                <Stack
                                  spacing={1}
                                  direction="row"
                                  alignItems="center"
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      fontSize: '12px',
                                      ...valueStyle,
                                    },
                                    '& .MuiInputBase-input.Mui-disabled': {
                                      color: 'rgba(0,0,0,0.6)',
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...fieldStyle,
                                      ...(hoveringIndex !== null && index === hoveringIndex
                                        ? { color: 'error.main' }
                                        : {}),
                                      minWidth: '60px',
                                    }}
                                    variant="body2"
                                  >
                                    GL code:
                                  </Typography>
                                  <> {(parsedFields & InvoiceGLItemParsedField.glCode) > 0 && bobAIIcon()}</>
                                  {hasEditAccess && canEdit ? (
                                    <Controller
                                      name={`invoiceGlValues.${index}.agoraSubcategoryId`}
                                      control={control}
                                      rules={{ required: !item.deleteFlag }}
                                      render={({ field }) => (
                                        <CategorySelector
                                          {...(facilityCategories?.length <= 0
                                            ? {
                                                ref: (el) => {
                                                  selectedFieldRef.current[index] = el;
                                                },
                                              }
                                            : {})}
                                          isCategory={false}
                                          currentCategory={watchCategory}
                                          currentSubcategory={watchSubcategory}
                                          currentGlCode={watchGlCode}
                                          existingGlCodes={watchExistingGls}
                                          handleChange={handleGlSubcategoryChange}
                                          disabled={!hasEditAccess || !invoiceDataLoaded}
                                          error={errors?.invoiceGlValues?.[index]?.agoraSubcategoryId}
                                          textFieldProps={{
                                            variant: 'standard',
                                            InputProps: {
                                              endAdornment: (
                                                <>
                                                  {!invoiceDataLoaded ? (
                                                    <CircularProgress color="inherit" size={16} />
                                                  ) : null}
                                                </>
                                              ),
                                              sx: {
                                                fontSize: '12px',
                                                ...valueStyle,
                                              },
                                            },
                                          }}
                                          item={item}
                                          index={index}
                                          facilityId={item?.facilityId}
                                        />
                                      )}
                                    />
                                  ) : (
                                    <TextField
                                      variant="standard"
                                      value={`${item.agoraSubcategory} ${item.glCode}`}
                                      disabled
                                      autoComplete="off"
                                      sx={{ flexGrow: 1 }}
                                      InputProps={{
                                        endAdornment: (
                                          <>
                                            {!invoiceDataLoaded ? <CircularProgress color="inherit" size={16} /> : null}
                                          </>
                                        ),
                                        sx: {
                                          fontSize: '12px',
                                          ...valueStyle,
                                        },
                                      }}
                                    />
                                  )}

                                  {facilityCategories?.length === 0 && hasEditAccess && canEdit && (
                                    <IconButton
                                      color="error"
                                      onMouseEnter={() => setHoveringIndex(index)}
                                      onMouseLeave={() => setHoveringIndex(null)}
                                      onClick={() => {
                                        handleDeleteGl(index);
                                        setHoveringIndex(null);
                                      }}
                                      sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        borderRadius: '50%',
                                        padding: 0.3,
                                        color: 'error.main',
                                        '&:hover': {
                                          backgroundColor: 'rgba(255, 0, 0, 0.15)',
                                        },
                                      }}
                                    >
                                      <HighlightOffIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  )}
                                </Stack>
                                {manualInvoice ? (
                                  <Controller
                                    name={`invoiceGlValues.${index}.amount`}
                                    control={control}
                                    rules={{
                                      required: { value: true, message: 'Required' },
                                      pattern: {
                                        value: /^[-+]?[0-9]*\.?[0-9]+$/,
                                        message: 'Please enter a valid number',
                                      },
                                    }}
                                    render={(field) => (
                                      <FieldMapping
                                        fieldName="GL Amount"
                                        name={`invoiceGlValues.${index}.amount`}
                                        fieldType={'currencyCalc'}
                                        value={watchAmount}
                                        showError={hoveringIndex !== null && index === hoveringIndex}
                                        onChange={(val) => {
                                          setValue(`invoiceGlValues.${index}.amount`, val);
                                          const updatedRow = { ...item, amount: val };
                                          setUnsavedData((prev) => ({
                                            ...prev,
                                            [item?.id]: updatedRow,
                                          }));
                                        }}
                                        control={control}
                                        disabled={!hasEditAccess || !canEdit || !invoiceDataLoaded}
                                      />
                                    )}
                                  />
                                ) : (
                                  <Accordion
                                    key={index}
                                    sx={{
                                      backgroundColor:
                                        highlightItem?.id !== 0 && highlightItem?.id === item.glItemId
                                          ? '#d8ebf9'
                                          : '#fff',
                                      mb: 0,
                                      flexGrow: 1,
                                    }}
                                    disableGutters
                                  >
                                    <AccordionSummary
                                      sx={{
                                        padding: 0,
                                        minHeight: 0,
                                        '&.MuiAccordionSummary-content': { margin: 0 },
                                        '& .MuiAccordionSummary-content': { margin: 0 },
                                      }}
                                      expandIcon={<ArrowDropDownIcon />}
                                    >
                                      <Stack spacing={1} direction={'row'} alignItems={'center'}>
                                        <Typography
                                          sx={{
                                            ...fieldStyle,
                                            ...(hoveringIndex !== null && index === hoveringIndex
                                              ? { color: 'error.main' }
                                              : {}),
                                          }}
                                          variant="standard"
                                        >
                                          Total amount:
                                        </Typography>
                                        {hasEditAccess && canEdit ? (
                                          <NumericFormat
                                            customInput={TextField}
                                            variant="standard"
                                            sx={{ flexGrow: 1 }}
                                            onValueChange={(values) => {
                                              const totalAmount = parseFloat(values.floatValue ?? 0);
                                              const newAmount = totalAmount - parseFloat(watchShipping);
                                              setValue(`invoiceGlValues.${index}.amount`, newAmount);
                                              const updatedRow = {
                                                ...item,
                                                amount: newAmount,
                                              };
                                              setUnsavedData((prev) => ({
                                                ...prev,
                                                [item?.id]: updatedRow,
                                              }));
                                            }}
                                            value={twoDecimal(parseFloat(watchAmount) + parseFloat(watchShipping))}
                                            onFocus={handleFocus}
                                            onBlur={handleBlur}
                                            error={!!errors?.invoiceGlValues?.[index]?.amount}
                                            helperText={errors?.invoiceGlValues?.[index]?.amount?.message}
                                            thousandSeparator
                                            decimalScale={2}
                                            fixedDecimalScale
                                            prefix="$"
                                            InputProps={{ sx: { fontSize: '12px' } }}
                                          />
                                        ) : (
                                          <Typography sx={valueStyle} variant="standard">
                                            {twoDecimal(parseFloat(watchAmount) + parseFloat(watchShipping))}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pb: 0 }}>
                                      <Stack spacing={1} direction={'row'} alignItems={'center'}>
                                        <Typography
                                          sx={{
                                            ...fieldStyle,
                                            ...(hoveringIndex !== null && index === hoveringIndex
                                              ? { color: 'error.main' }
                                              : {}),
                                          }}
                                          variant="standard"
                                        >
                                          Amount:
                                        </Typography>

                                        {hasEditAccess && canEdit ? (
                                          <Controller
                                            name={`invoiceGlValues.${index}.amount`}
                                            control={control}
                                            rules={{
                                              required: { value: true, message: 'Required' },
                                              pattern: {
                                                value: /^[-+]?[0-9]*\.?[0-9]+$/,
                                                message: 'Please enter a valid number',
                                              },
                                            }}
                                            render={(field) => (
                                              <NumericFormat
                                                {...field}
                                                customInput={TextField}
                                                sx={{ flexGrow: 1 }}
                                                variant="standard"
                                                onValueChange={(values) => {
                                                  setValue(`invoiceGlValues.${index}.amount`, values.floatValue ?? 0);
                                                  const updatedRow = {
                                                    ...item,
                                                    amount: values.floatValue ?? 0,
                                                  };
                                                  setUnsavedData((prev) => ({
                                                    ...prev,
                                                    [item?.id]: updatedRow,
                                                  }));
                                                }}
                                                value={watchAmount}
                                                onFocus={handleFocus}
                                                onBlur={handleBlur}
                                                error={!!errors?.invoiceGlValues?.[index]?.amount}
                                                helperText={errors?.invoiceGlValues?.[index]?.amount?.message}
                                                thousandSeparator
                                                decimalScale={2}
                                                fixedDecimalScale
                                                prefix="$"
                                                InputProps={{ sx: { fontSize: '12px' } }}
                                              />
                                            )}
                                          />
                                        ) : (
                                          <NumericFormat
                                            customInput={TextField}
                                            sx={{ flexGrow: 1 }}
                                            variant="standard"
                                            disabled
                                            value={watchAmount}
                                            thousandSeparator
                                            decimalScale={2}
                                            fixedDecimalScale
                                            prefix="$"
                                            InputProps={{ sx: { fontSize: '12px' } }}
                                          />
                                        )}
                                      </Stack>
                                      <Stack spacing={1} direction={'row'} alignItems={'center'}>
                                        <Typography
                                          sx={{
                                            ...fieldStyle,
                                            ...(hoveringIndex !== null && index === hoveringIndex
                                              ? { color: 'error.main' }
                                              : {}),
                                          }}
                                          variant="standard"
                                        >
                                          Shipping:
                                        </Typography>
                                        {hasEditAccess && canEdit ? (
                                          <Controller
                                            name={`invoiceGlValues.${index}.shipping`}
                                            control={control}
                                            rules={{
                                              required: { value: true, message: 'Required' },
                                              pattern: {
                                                value: /^[-+]?[0-9]*\.?[0-9]+$/,
                                                message: 'Please enter a valid number',
                                              },
                                            }}
                                            render={(field) => (
                                              <NumericFormat
                                                {...field}
                                                customInput={TextField}
                                                sx={{ flexGrow: 1 }}
                                                variant="standard"
                                                onValueChange={(values) => {
                                                  setValue(`invoiceGlValues.${index}.shipping`, values.floatValue ?? 0);
                                                  const updatedRow = {
                                                    ...item,
                                                    shipping: values.floatValue ?? 0,
                                                  };
                                                  setUnsavedData((prev) => ({
                                                    ...prev,
                                                    [item?.id]: updatedRow,
                                                  }));
                                                }}
                                                value={watchShipping}
                                                onFocus={handleFocus}
                                                onBlur={handleBlur}
                                                error={!!errors?.invoiceGlValues?.[index]?.shipping}
                                                helperText={errors?.invoiceGlValues?.[index]?.shipping?.message}
                                                thousandSeparator
                                                decimalScale={2}
                                                fixedDecimalScale
                                                prefix="$"
                                                InputProps={{ sx: { fontSize: '12px' } }}
                                              />
                                            )}
                                          />
                                        ) : (
                                          <NumericFormat
                                            customInput={TextField}
                                            sx={{ flexGrow: 1 }}
                                            variant="standard"
                                            disabled
                                            value={watchShipping}
                                            decimalScale={2}
                                            fixedDecimalScale
                                            prefix="$"
                                            InputProps={{ sx: { fontSize: '12px' } }}
                                          />
                                        )}
                                      </Stack>
                                    </AccordionDetails>
                                  </Accordion>
                                )}
                                <Stack
                                  direction={'row'}
                                  alignItems={'center'}
                                  sx={{ '&.MuiStack-root': { mt: 0 } }}
                                  spacing={1}
                                >
                                  <Typography
                                    sx={{
                                      ...fieldStyle,
                                      ...(hoveringIndex !== null && index === hoveringIndex
                                        ? { color: 'error.main' }
                                        : {}),
                                    }}
                                    variant="standard"
                                  >
                                    Memo:
                                  </Typography>{' '}
                                  <>{(parsedFields & InvoiceGLItemParsedField.memo) > 0 && bobAIIcon()}</>
                                  {hasEditAccess && canEdit ? (
                                    <TextField
                                      sx={{ flexGrow: 1 }}
                                      InputProps={{ sx: { fontSize: '12px' } }}
                                      onInput={(e) => {
                                        setValue(`invoiceGlValues.${index}.memo`, e?.target?.value);
                                        const updatedRow = {
                                          ...item,
                                          memo: e?.target?.value,
                                        };
                                        setUnsavedData((prev) => ({
                                          ...prev,
                                          [item?.id]: updatedRow,
                                        }));
                                      }}
                                      value={watchMemo}
                                      disabled={!hasEditAccess || !invoiceDataLoaded}
                                      variant="standard"
                                      error={errors?.invoiceGlValues?.[index]?.memo}
                                      autoComplete="off"
                                    />
                                  ) : (
                                    <TextField
                                      variant="standard"
                                      value={item.memo}
                                      disabled
                                      sx={{ flexGrow: 1 }}
                                      InputProps={{ sx: valueStyle }}
                                      autoComplete="off"
                                    />
                                  )}
                                </Stack>
                              </Stack>
                            </ListItem>{' '}
                          </FieldMappingProvider>
                          {fields.length > 1 && index < fields.length - 1 && (
                            <Divider variant="fullWidth" sx={dividerStyle} />
                          )}
                        </Box>
                      </Fragment>
                    );
                  })}
                </>
              </List>
            </Scrollbar>
          )}
          {editGlItems && (
            <Grid container direction="row" sx={{ width: 'calc(100% - 10px)', mx: '5px', mb: 1, mt: 0 }} spacing={1}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  type="button"
                  fullWidth
                  onClick={() => {
                    resetGlData();
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6} sx={{ pr: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  type="submit"
                  fullWidth
                  disabled={!hasEditAccess || !invoiceDataLoaded}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
        {confirmEdit && (
          <Dialog
            open
            maxWidth="lg"
            onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
              setConfirmEdit(false);
            }}
          >
            <DialogTitle sx={{ pb: 2, textAlign: 'center' }}> Confirm GL Edit</DialogTitle>
            <DialogContent sx={{ typography: 'body2' }}>
              <Box> Saving these GLs will lock the invoice items.</Box>
              <br />
              <Box>If you want to edit the invoice items later,</Box>
              <Box>
                you'll need to click "Unlock Invoice Items" in the{' '}
                <Box component="span" fontWeight="bold">
                  Invoice Details
                </Box>{' '}
                section &nbsp;-&nbsp; this will remove the saved GLs.
              </Box>
              <br />
              <Box>Do you want to continue?</Box>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color={'error'}
                onClick={() => {
                  setConfirmEdit(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={'primary'}
                onClick={() => {
                  setConfirmEdit(false);
                  handleGlsFormSave();
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </>
  );
}

const dividerStyle = {
  borderColor: 'info.main',
  height: '1px',
  my: 1,
  borderStyle: 'dashed',
};
const mainBoxStyle = {
  borderRadius: '4px',
  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  backgroundColor: '#fff',
  overflow: 'auto',
  border: 'solid 1px #c1c9d0',
};
const fieldStyle = {
  color: '#060606',
  fontSize: '14px',
  display: 'inline-block',
  whiteSpace: 'nowrap',
};
const valueStyle = { fontSize: '12px', color: '#060606' };
const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 10px',
  borderColor: 'info.main',
};
